import { useEffect } from "react";
import { WISHLIST_CATEGORY_CONSTANTS } from "../_MODULES/_API";
import { WISHLIST_CATEGORY_OPTIONS } from "../_UTILS/wishlist.util";
import { AppModal } from "../../../../../_CORE/APP/Templates/_UI/Modal/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import {
    FormGrid, FieldInput, FieldTextarea, FieldColor, FieldSwitch, FieldSelect, useForm,
} from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { PALETTE_HEX } from "../../../_Catalogs.Views";
import { isDuplicateRecord } from "../../../../_UI/recordUniqueness";

const COMMON_CATEGORIES = new Set(WISHLIST_CATEGORY_OPTIONS.filter((item) => item.value !== "OTHER").map((item) => item.value));
const ICON_BY_CATEGORY = Object.fromEntries(WISHLIST_CATEGORY_OPTIONS.map((item) => [item.value, item.icon || ""]));

const EMPTY_VALUES = {
    categoryChoice: "",
    categoryOther: "",
    description: "",
    color: "#e83a52",
    icon: "",
    isFavorite: false,
};

export const WishlistCategoryModal = ({ isOpen, category = null, categories = [], onClose, onSaved }) => {
    const IS_EDIT = !!category?.id;
    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 250,
    });

    const FORM = useForm({
        initialValues: EMPTY_VALUES,
        schema: { categoryChoice: { required: true } },
        onSubmit: async (values) => {
            const name = values.categoryChoice === "OTHER"
                ? String(values.categoryOther || "").trim()
                : String(values.categoryChoice || "").trim();

            if (!name) {
                FORM.setError(values.categoryChoice === "OTHER" ? "categoryOther" : "categoryChoice", "Elegí o escribí una categoría", { focus: true });
                return;
            }
            if (isDuplicateRecord({ rows: categories, value: name, currentId: category?.id })) {
                FORM.setError(values.categoryChoice === "OTHER" ? "categoryOther" : "categoryChoice", "Ya existe una categoría con ese nombre", { focus: true });
                return;
            }

            const suggestedIcon = ICON_BY_CATEGORY[values.categoryChoice] || "";
            const payload = {
                name,
                description: values.description || undefined,
                color: values.color || undefined,
                icon: values.icon || suggestedIcon || undefined,
                isFavorite: !!values.isFavorite,
            };
            const response = IS_EDIT
                ? await actionMutate({ method: "PATCH", endpoint: WISHLIST_CATEGORY_CONSTANTS.UPDATE, id: category.id, data: payload, successMessage: "Categoría actualizada" })
                : await actionMutate({ method: "POST", endpoint: WISHLIST_CATEGORY_CONSTANTS.CREATE, data: payload, successMessage: "Categoría creada" });
            if (!response.success) { FORM.applyApiError(response); return; }
            onSaved?.(response.data, IS_EDIT);
            onClose?.();
        },
    });

    useEffect(() => {
        if (!isOpen) return;
        const rawName = String(category?.name || "").trim();
        FORM.reset(IS_EDIT ? {
            categoryChoice: COMMON_CATEGORIES.has(rawName) ? rawName : "OTHER",
            categoryOther: COMMON_CATEGORIES.has(rawName) ? "" : rawName,
            description: category.description ?? "",
            color: category.color ?? "#e83a52",
            icon: category.icon ?? "",
            isFavorite: !!category.isFavorite,
        } : EMPTY_VALUES);
    }, [isOpen, category]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <AppModal isOpen={isOpen} onClose={onClose} isDirty={FORM.isDirty} size="lg"
            title={IS_EDIT ? "Editar categoría" : "Nueva categoría"}
            description="Elegí una categoría común para escribir menos o usá Otro si necesitás una personalizada."
            footer={<>
                <AppButton variant="ghost" tone="neutral" onClick={onClose}>Cancelar</AppButton>
                <AppButton onClick={FORM.onSubmit} isLoading={useActionApiIsLoading}>{IS_EDIT ? "Guardar cambios" : "Crear categoría"}</AppButton>
            </>}>

            <div className="LStudio__app_priority_toggle">
                <FieldSwitch id="wishlist_category_favorite" label="Destacado" text="Mostrar esta categoría primero"
                    isChecked={!!FORM.values.isFavorite}
                    onChange={(event) => FORM.setValue("isFavorite", event.target.checked)} />
            </div>

            <FormGrid columns={2}>
                <FieldSelect {...FORM.field("categoryChoice")} label="Categoría"
                    options={WISHLIST_CATEGORY_OPTIONS} placeholder="Elegí una categoría" isSearchable span={2} />
                {FORM.values.categoryChoice === "OTHER" && <FieldInput {...FORM.field("categoryOther")}
                    label="Otra categoría" placeholder="Ej: Fotografía, herramientas de taller, camping..." span={2} />}
                <FieldTextarea {...FORM.field("description")} label="Descripción" rows={3}
                    placeholder="Ej: Cosas para el living y la cocina" span={2} />
                <FieldColor {...FORM.field("color")} label="Color" swatches={PALETTE_HEX} />
                <FieldInput {...FORM.field("icon")} label="Icono o emoji" placeholder="Opcional. Ej: 🛋️" />
            </FormGrid>
        </AppModal>
    );
};
